import { getVariableMeta } from './textTemplateVariables'

const KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_.]*$/
const LEGACY_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*(->[a-zA-Z][a-zA-Z0-9_]*)*$/

export function getVariableKey(variable) {
  if (typeof variable === 'string') return variable
  return variable?.key ?? ''
}

export function validateVariableKey(key, legacy = false) {
  if (!key || typeof key !== 'string') {
    return { valid: false, error: 'Key is required.' }
  }
  const trimmed = key.trim()
  const pattern = legacy ? LEGACY_KEY_PATTERN : KEY_PATTERN
  if (!pattern.test(trimmed)) {
    return {
      valid: false,
      error: legacy
        ? 'Key must start with a letter and use letters, numbers, underscores, or -> segments.'
        : 'Key must start with a letter and contain only letters, numbers, dots, or underscores.',
    }
  }
  return { valid: true, key: trimmed }
}

export function normalizeCustomVariable(variable, legacy = false) {
  if (typeof variable === 'string') {
    const key = legacy ? variable.replace(/^user->/, 'user.') : variable
    const meta = getVariableMeta(key)
    return {
      key: variable,
      label: meta.label,
      type: meta.type || 'string',
      group: 'custom',
      example: meta.example || '',
      custom: true,
    }
  }

  return {
    key: variable.key,
    label: variable.label || getVariableMeta(variable.key).label,
    type: variable.type || 'string',
    group: 'custom',
    example: variable.example || '',
    custom: true,
  }
}

export function splitCustomVariables(savedVariables = [], catalogKeys = []) {
  const catalogSet = new Set(catalogKeys)

  return (savedVariables || [])
    .map((variable) => {
      if (typeof variable === 'string') {
        if (catalogSet.has(variable)) return null
        return normalizeCustomVariable(variable, true)
      }
      const key = getVariableKey(variable)
      if (variable.custom === true || !catalogSet.has(key)) {
        return normalizeCustomVariable(variable)
      }
      return null
    })
    .filter(Boolean)
}

export function mergeTemplateVariables(customVariables = [], parsedFromContent = []) {
  const merged = new Map()

  customVariables.forEach((variable) => {
    const normalized = normalizeCustomVariable(variable)
    merged.set(normalized.key, normalized)
  })

  parsedFromContent.forEach((variable) => {
    const key = getVariableKey(variable)
    if (!key) return
    const existing = merged.get(key)
    if (existing?.custom) {
      merged.set(key, { ...existing, ...variable, custom: true })
    } else {
      merged.set(key, typeof variable === 'object' ? variable : getVariableMeta(key))
    }
  })

  return Array.from(merged.values())
}

export function buildInsertVariableList(catalog = [], customVariables = []) {
  const catalogKeys = new Set(catalog.map((v) => v.key))
  const customOnly = customVariables
    .map((v) => normalizeCustomVariable(v))
    .filter((v) => !catalogKeys.has(v.key))

  return [...customOnly, ...catalog]
}

export function formatEmailCatalogVariable(variable) {
  return {
    key: variable.key,
    label: variable.description || variable.label || variable.key,
    type: variable.type || 'string',
    group: variable.group || 'general',
    example: variable.example || '',
  }
}

export const EMAIL_TEMPLATE_VARIABLES = [
  { key: 'user->name', description: "User's full name", example: 'John Doe', group: 'user' },
  { key: 'user->email', description: "User's email address", example: 'john@example.com', group: 'user' },
  { key: 'user->firstName', description: "User's first name", example: 'John', group: 'user' },
  { key: 'user->lastName', description: "User's last name", example: 'Doe', group: 'user' },
  { key: 'key', description: 'Unique token/key for links', example: 'abc123xyz', group: 'link' },
  { key: 'url', description: 'Application base URL', example: 'https://app.example.com', group: 'link' },
  { key: 'app_name', description: 'Application name', example: 'DME Platform', group: 'company' },
  { key: 'date', description: 'Current year', example: '2026', group: 'date' },
]

export function getEmailVariableCatalog() {
  return EMAIL_TEMPLATE_VARIABLES.map(formatEmailCatalogVariable)
}

export function parseEmailVariablesFromContent(content, subject = '') {
  if (!content && !subject) return []

  const found = new Map()
  const combined = `${subject}\n${content}`
  const regex = /\{\{\s*\$([a-zA-Z_][a-zA-Z0-9_]*(?:->[a-zA-Z0-9_]+)*)\s*\}\}/g
  let match

  while ((match = regex.exec(combined)) !== null) {
    const key = match[1]
    if (!found.has(key)) {
      const catalogEntry = EMAIL_TEMPLATE_VARIABLES.find((v) => v.key === key)
      found.set(
        key,
        catalogEntry
          ? formatEmailCatalogVariable(catalogEntry)
          : normalizeCustomVariable(key, true),
      )
    }
  }

  return Array.from(found.values())
}

export function buildSampleDataFromVariables(variables = []) {
  const sample = {}
  variables.forEach((variable) => {
    const key = getVariableKey(variable)
    if (!key) return
    const example = typeof variable === 'object' ? variable.example : null
    sample[key] = example || `[${key}]`
  })
  return sample
}
